<template>
    <div v-if="loading == false">
        <UnapprovedClientsList
            :data_source="rows"
            :target_page="targetPage"
            :createAction="false"
            @applyCriteria="modifyCriteria"
            :loadingColumns="loadingTable"
            :column_meta="columnMeta"
            :totalCount="totalCount"
            :storeKey="storeKey"
            :useOverride="useOverride"
            @cancelOverride="handleCancelOverride"
            @approveClients="approveClients"
            :matchLoading="matchLoading"
            @runMatching="runMatching"
            :matchStatusFilter="matchStatusFilter"
            @matchStatusFilterChanged="onMatchStatusFilter"
            :approvalStatusFilter="approvalStatusFilter"
            @approvalStatusFilterChanged="onApprovalStatusFilter"
        >
        </UnapprovedClientsList>
    </div>
    <div v-else>Loading...</div>
</template>

<script>
    import UnapprovedClientsList from '@/components/general/list/UnapprovedClientsList.vue';
    import { tryGetFilter } from '@/util/tryGetFilter';
    import { clients } from '@/util/apiRequests';

    export default {
        name: 'UnapprovedClients',
        components: { UnapprovedClientsList },
        props: {
            overrideFilter: {
                type: Object,
                default: null,
            },
        },
        data() {
            return {
                rows: [],
                targetPage: 'ClientOverview',
                loading: true,
                loadingTable: false,
                useOverride: false,
                showNewClientModal: false,
                totalCount: 0,
                storeKey: 'unapprovedClients',
                export_api: '/clients',
                columnMeta: [
                    { field: 'first_name', headerText: 'First Name' },
                    { field: 'last_name', headerText: 'Last Name' },
                    { field: 'email', headerText: 'Email' },
                    { field: 'phone', headerText: 'Phone Number' },
                ],
                matchLoading: false,
                matchStatusFilter: '',
                approvalStatusFilter: '',
            };
        },
        computed: {
            criteria() {
                return this.useOverride && this.overrideFilter ? this.overrideCriteria : this.savedCriteria;
            },
            savedCriteria() {
                return tryGetFilter(this.$store, this.storeKey);
            },
            overrideCriteria() {
                return this.$store.getters['filters/overrideCriteria'](this.storeKey);
            },
        },
        methods: {
            async load_data() {
                try {
                    let statusFilter;
                    if (this.approvalStatusFilter === 'unapproved') {
                        statusFilter = ['Unapproved'];
                    } else if (this.approvalStatusFilter === 'approved') {
                        statusFilter = ['Active'];
                    } else {
                        statusFilter = ['Unapproved', 'Active'];
                    }

                    const body = {
                        criteria: {
                            ...this.criteria,
                            match_status: this.matchStatusFilter || undefined,
                            filter: {
                                'clients.status': this.approvalStatusFilter ? statusFilter : (this.criteria?.filter?.status ?? statusFilter),
                            },
                        },
                        activate_status: 0,
                    };

                    const res = await this.$api.post(this.export_api, body);

                    if (res.status >= 400) {
                        this.$toasted.error('Failed to fetch clients roster.');
                        return;
                    }

                    const data = res.data.rows
                        ?.slice()
                        .reverse()
                        .map((item) => {
                            const objData = { ...item };
                            delete objData.dob;
                            delete objData.counselor;
                            delete objData.guardian_name;
                            delete objData.tags;
                            delete objData.matched_fields;
                            delete objData.dashboard_name;
                            delete objData.kiosk_dashboard_name;
                            delete objData.mobex_plus_dashboard_name;
                            delete objData.mhh_dashboard_name;
                            delete objData.starkid_dashboard_name;
                            delete objData.family_connect_dashboard_name;
                            delete objData.mobex_health_home_dashboard_name;
                            return objData;
                        });

                    this.rows = data;
                    this.totalCount = res.data.total_count;
                } catch (err) {
                    console.error(err);
                    this.$toasted.error('Failed to fetch clients roster.');
                }

                this.$nextTick(() => {
                    this.loading = false;
                });
            },
            async init() {
                if (this.overrideFilter) {
                    this.useOverride = true;
                    this.$store.commit('filters/applyOverride', {
                        stateKey: this.storeKey,
                        criteria: this.overrideFilter,
                    });
                }
            },
            async modifyCriteria(criteria) {
                if (this.targetPage) {
                    this.$store.commit('filters/update', {
                        criteriaPage: this.targetPage,
                        payload: criteria,
                    });
                }
                this.criteriaProps = criteria;
                await this.load_data();
            },
            handleCancelOverride() {
                this.useOverride = false;
            },
            async runMatching() {
                try {
                    this.matchLoading = true;
                    const endpoint = clients.eligibilityMatch();
                    const res = await this.$api.post(endpoint, {});
                    if (res.data.success) {
                        const s = res.data.summary;
                        const parts = [];
                        if (s.autoApproved) parts.push(`${s.autoApproved} full match`);
                        if (s.autoActivated) parts.push(`${s.autoActivated} auto-activated`);
                        if (s.partial) parts.push(`${s.partial} partial`);
                        if (s.noMatch) parts.push(`${s.noMatch} no match`);
                        if (s.notFound) parts.push(`${s.notFound} not found`);
                        if (s.notEligible) parts.push(`${s.notEligible} not eligible`);
                        if (s.skippedReviewed) parts.push(`${s.skippedReviewed} skipped (reviewed)`);
                        if (s.errored) parts.push(`${s.errored} errored`);
                        const msg = parts.length > 0 ? parts.join(', ') : 'No unapproved members found';
                        this.$toasted.success(`Matching complete: ${msg}`);
                        await this.load_data();
                    }
                } catch (err) {
                    console.error(err);
                    this.$toasted.error('Failed to run eligibility matching');
                } finally {
                    this.matchLoading = false;
                }
            },
            onApprovalStatusFilter(event) {
                this.approvalStatusFilter = event.target.value;
                this.$store.commit('filters/updateFilter', {
                    stateKey: this.storeKey,
                    criteria: {
                        ...this.criteria,
                        page: { num_per_page: this.criteria?.page?.num_per_page || 10, page_num: 1 },
                    },
                });
            },
            onMatchStatusFilter(event) {
                this.matchStatusFilter = event.target.value;
                this.$store.commit('filters/updateFilter', {
                    stateKey: this.storeKey,
                    criteria: {
                        ...this.criteria,
                        page: { num_per_page: this.criteria?.page?.num_per_page || 10, page_num: 1 },
                    },
                });
            },
            async approveClients(data) {
                try {
                    this.loadingTable = true;
                    let endpoint = clients.approveClients();
                    let res = await this.$api.post(endpoint, data);
                    if (res.data.success) {
                        await this.load_data();
                        this.$toasted.success(`Member ${data.activate_status ? 'approved' : 'Denied'} Successfully`);
                    }
                } catch (err) {
                    const errMsg = err?.response?.data?.message || err?.message || 'Failed to approve members';
                    this.$toasted.error(errMsg);
                } finally {
                    this.loadingTable = false;
                }
            },
        },
        async created() {
            await this.init();
            await this.load_data();
        },
        watch: {
            criteria: {
                deep: true,
                async handler() {
                    await this.load_data();
                },
            },
        },
    };
</script>
